import React, { useState, useEffect } from "react";
import { TextInput, TextArea } from "../TextInput";
import { SelectBox } from "../SelectBox";
import { useIncidentForm } from "./useIncidentForm";
import {issueLogEntry, getIssueLogConfig} from "../../api/AWSAPI";
import {
    priorityOptions,
    impactOptions,
    categoryOptions
} from "./incidentSchema";

function getSafeIncidentUrl(recordLink) {
    if (typeof recordLink !== "string" || !recordLink.trim()) {
        return "";
    }

    try {
        const url = new URL(recordLink);
        if (url.protocol !== "https:" && url.protocol !== "http:") {
            return "";
        }
        return url.href;
    } catch {
        return "";
    }
}

function IncidentModal({ modal, onClose, userEmail, userName }) {
    const [confirmation, setConfirmation] = useState(false);
    const [incidentNumber, setIncidentNumber] = useState("");
    const [incidentUrl, setIncidentUrl] = useState("");
    const [systemOptions, setSystemOptions] = useState([]);
    const [errorTxt, setErrorTxt] = useState("");

    const {
        formData,
        errors,
        isSubmitting,
        setIsSubmitting,
        updateField,
        validateForm,
        resetForm,
    } = useIncidentForm({
        name: userName || '',
        email: userEmail || ''
    });

    // Fetch issue log config when modal opens
    useEffect(() => {
        if (modal && systemOptions.length === 0) {
            getIssueLogConfig((response) => {
                if (response && response.success && response.items) {
                    const options = response.items.map(item => ({
                        value: item.name,
                        label: item.name
                    }));
                    setSystemOptions(options);
                }
            });
        }
    }, [modal]);

    // Reset form when modal opens/closes or user data changes
    useEffect(() => {
        if (modal) {
            resetForm({ name: userName || '', email: userEmail || '' });
            setConfirmation(false);
            setIncidentNumber("");
            setIncidentUrl("");
            setErrorTxt("");
        }
    }, [modal, userName, userEmail, resetForm]);

    const handleClose = () => {
        resetForm({ name: userName || '', email: userEmail || '' });
        setConfirmation(false);
        setIncidentNumber("");
        setIncidentUrl("");
        setErrorTxt("");
        onClose();
    };

    const handleIncidentResponse = (data) => {
        const reference = data && data.display_value;
        const isSuccess = data && data.success !== false && !data.error;

        if (isSuccess && reference) {
            setIncidentUrl(getSafeIncidentUrl(data.record_link));
            setIncidentNumber(reference);
            setConfirmation(true);
            setIsSubmitting(false);
            return;
        }

        setErrorTxt(data?.errorTxt || data?.message || "Unable to log issue");
        setConfirmation(true);
        setIsSubmitting(false);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const isValid = validateForm();
        if (!isValid) {
            setIsSubmitting(false);
            return;
        }
        issueLogEntry(formData, (response) => {
            handleIncidentResponse(response)
        });
    };

    if (!modal) return null;

    return (

        <div className="modal-shadow">
            <div className="incident-modal">
                <div className="ms-close">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        onClick={handleClose}
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
                <div className="incident-form">
                    {!confirmation && (
                        <>
                            <div className="row">
                                <div className="col-sm-6">
                                    <TextInput
                                        name="name"
                                        type="text"
                                        label="Your Name"
                                        value={formData.name}
                                        maxLength={45}
                                        asterix={true}
                                        onChange={(name, value) => updateField('name', value)}
                                        error={errors.name}
                                    />
                                </div>
                                <div className="col-sm-6">
                                    <TextInput
                                        name="email"
                                        type="email"
                                        label="Your Email"
                                        value={formData.email}
                                        maxLength={45}
                                        asterix={true}
                                        onChange={(name, value) => updateField('email', value)}
                                        error={errors.email}
                                    />
                                </div>
                                <div className="col-sm-12">
                                    <SelectBox
                                        name="system"
                                        label="System"
                                        placeholder="Select System"
                                        value={formData.system}
                                        maxLength={45}
                                        options={systemOptions}
                                        onChange={(name, value) => updateField('system', value)}
                                        error={errors.system}
                                    />
                                </div>

                                <div className="col-sm-6">
                                    <SelectBox
                                        name="priority"
                                        label="Priority"
                                        placeholder="Select Priority"
                                        value={formData.priority}
                                        maxLength={45}
                                        options={priorityOptions}
                                        onChange={(name, value) => updateField('priority', value)}
                                        error={errors.priority}
                                    />
                                </div>
                                <div className="col-sm-6">
                                    <SelectBox
                                        name="impact"
                                        label="Impact"
                                        placeholder="Select Impact"
                                        value={formData.impact}
                                        maxLength={45}
                                        options={impactOptions}
                                        onChange={(name, value) => updateField('impact', value)}
                                        error={errors.impact}
                                    />
                                </div>

                                <div className="col-sm-6">
                                    <SelectBox
                                        name="category"
                                        label="Category"
                                        placeholder="Select Category"
                                        value={formData.category}
                                        maxLength={45}
                                        options={categoryOptions}
                                        onChange={(name, value) => updateField('category', value)}
                                        error={errors.category}
                                    />
                                </div>

                                <div className="col-sm-12">
                                    <fieldset className="ln-c-form-group radio-group">
                                        <legend className="ln-c-label">What type of issue is being reported?</legend>
                                        <div>
                                            <input type="radio" id="customerService" name="issueType" value="customerService" checked={formData.issueType === 'customerService'} onChange={(e) => updateField('issueType', e.target.value)} />
                                            <label htmlFor="customerService">Customer Service: This is single Customer/Card/Order issue e.g. a card not working</label>
                                        </div>
                                        <div>
                                            <input type="radio" id="request" name="issueType" value="request" checked={formData.issueType === 'request'} onChange={(e) => updateField('issueType', e.target.value)} />
                                            <label htmlFor="request">Question/Info: This is a request for information </label>
                                        </div>
                                        <div>
                                            <input type="radio" id="incident" name="issueType" value="incident" checked={formData.issueType === 'incident'} onChange={(e) => updateField('issueType', e.target.value)} />
                                            <label htmlFor="incident">Incident: This is an Outage/Problem affecting Multiple Customers or Multiple Orders e.g. API not working</label>
                                        </div>
                                    </fieldset>
                                    <div className="input-meta">
                                        {errors.issueType ? <div className="invalid-error input-meta--error">{errors.issueType}</div> : ''}
                                    </div>
                                </div>

                                <div className="col-sm-12">
                                    <TextInput
                                        name="subject"
                                        type="text"
                                        label="Short Description"
                                        value={formData.subject}
                                        asterix={true}
                                        onChange={(name, value) => updateField('subject', value)}
                                        error={errors.subject}
                                    />
                                </div>
                                <div className="col-sm-12">
                                    <TextArea
                                        name="comments"
                                        type="textarea"
                                        label="Further Details"
                                        value={formData.comments}
                                        maxLength={8000}
                                        rows={5}
                                        asterix={true}
                                        onChange={(name, value) => updateField('comments', value)}
                                        error={errors.comments}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12 py-4">
                                    <button
                                        disabled={isSubmitting}
                                        className="ln-c-button ln-c-button--filled"
                                        onClick={handleSubmit}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                    {confirmation && (
                        <>
                            <div className="row">
                                <div className="col-sm-12 py-4">
                                    <p className="text-center">
                                        Thank you, your issue has been logged.
                                    </p>
                                    {incidentNumber && incidentUrl ? (
                                        <>
                                            <a
                                                className="incident-link"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={incidentUrl}
                                            >
                                                {incidentNumber}
                                            </a>
                                            <p className="text-center">
                                                Please click the incident number link above to attach
                                                any relevant documents.
                                            </p>
                                        </>
                                    ) : incidentNumber ? (
                                        <>
                                            <p className="text-center incident-reference">
                                                {incidentNumber}
                                            </p>
                                            <p className="text-center">
                                                Please quote this reference if you need to follow up on this issue.
                                            </p>
                                        </>
                                    ) : errorTxt ? (
                                        <p className="text-center text-danger">
                                            There was an error generating your incident link. Please try again later.
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12 py-4">
                                    <button
                                        className="ln-c-button ln-c-button--filled"
                                        onClick={handleClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default IncidentModal;
